/**
 * AddToTenantDialog Component (ORCH-336, ORCH-21 §12 #75; PRD User Manager UM-15)
 *
 * Purpose: A system admin in GLOBAL gives a person access to another tenant:
 *          pick the tenant, then the Company, roles and security attributes
 *          they get there. The same MembershipForm as the user page and invites.
 * Logic: Only tenants the person isn't in are offered. The server creates the
 *        membership (PUT /users/{id}/memberships/{tenant}) and applies the same
 *        role and scope checks as an invite.
 * Dependencies: MembershipForm, userManagementService
 */

import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from '@mui/material';
import { LoadingButton } from '../common';
import type { RoleOption } from '../common';
import { authzService } from '../../services/authzService';
import { PermissionKey } from '../../constants/permissionKeys';
import userManagementService, { userErrorMessage } from '../../services/userManagementService';
import { asList } from '../../utils/listValues';
import MembershipForm from './MembershipForm';
import { cleanMetadata, MembershipDraft, COMPANY_FIELD } from './membership';
import { fieldGridSx } from '../form-layout/fieldGrid';
import { SecondaryButton } from '../common/ActionButtons';

export interface AddToTenantDialogProps {
  open: boolean;
  userId: string;
  personName: string;
  /** Tenants the person isn't in yet. */
  tenants: { tenant_id: string; name: string }[];
  attributeFieldIds: string[];
  onClose: () => void;
  onAdded: (tenantId: string) => void;
}

const EMPTY: MembershipDraft = { roleIds: [], metadata: {} };

const AddToTenantDialog: React.FC<AddToTenantDialogProps> = ({
  open,
  userId,
  personName,
  tenants,
  attributeFieldIds,
  onClose,
  onAdded,
}) => {
  const [tenantId, setTenantId] = useState('');
  const [draft, setDraft] = useState<MembershipDraft>(EMPTY);
  const [initial, setInitial] = useState<MembershipDraft>(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The chosen tenant's roles: its own as well as the standard ones (ORCH-398)
  const [roles, setRoles] = useState<RoleOption[]>([]);

  useEffect(() => {
    if (!tenantId) {
      setRoles([]);
      return;
    }
    let current = true;
    authzService
      .listRoles(userId, tenantId)
      .then((list) => current && setRoles(list))
      .catch(() => current && setRoles([]));
    return () => {
      current = false;
    };
  }, [tenantId, userId]);

  useEffect(() => {
    if (open) {
      setTenantId('');
      setDraft(EMPTY);
      setInitial({ roleIds: [], metadata: {} });
      setError(null);
    }
  }, [open]);

  const ready =
    !!tenantId && draft.roleIds.length > 0 && asList(draft.metadata[COMPANY_FIELD]).length > 0;

  const handleAdd = async () => {
    setSaving(true);
    setError(null);
    try {
      await userManagementService.updateMembership(
        userId,
        tenantId,
        cleanMetadata(draft.metadata),
        { roleIds: draft.roleIds }
      );
      onAdded(tenantId);
    } catch (e) {
      setError(userErrorMessage(e, 'The person could not be added to the tenant.'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="md"
      fullWidth
      data-testid="add-to-tenant-dialog"
    >
      <DialogTitle>Add {personName} to a Tenant</DialogTitle>
      <DialogContent
        sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '8px !important' }}
      >
        {error && (
          <Alert severity="error" data-testid="add-to-tenant-error">
            {error}
          </Alert>
        )}
        <Box sx={fieldGridSx(1)}>
          <FormControl fullWidth required>
            <InputLabel id="add-to-tenant-label">Tenant</InputLabel>
            <Select
              labelId="add-to-tenant-label"
              label="Tenant"
              value={tenantId}
              onChange={(event) => setTenantId(event.target.value)}
              data-testid="add-to-tenant-select"
            >
              {tenants.map((tenant) => (
                <MenuItem key={tenant.tenant_id} value={tenant.tenant_id}>
                  {tenant.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        {tenantId && (
          <MembershipForm
            key={tenantId}
            tenantId={tenantId}
            roles={roles}
            initial={initial}
            value={draft}
            onChange={setDraft}
            permission={PermissionKey.USER_CREATE}
            canAssignRoles
            attributeFieldIds={attributeFieldIds}
            warnNoRoles={false}
          />
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <SecondaryButton onClick={onClose} disabled={saving}>
          Cancel
        </SecondaryButton>
        <LoadingButton
          variant="contained"
          loading={saving}
          disabled={!ready}
          onClick={handleAdd}
          data-testid="add-to-tenant-confirm"
        >
          Add to Tenant
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default AddToTenantDialog;
